import React, { useState } from "react";
import { Button } from "./common/ui";

const KL = { color: "var(--text-muted)", fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" };

export default function StatsCards({ stats }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("setting_stats_collapsed") === "true"; } catch (e) { return false; }
  });

  const toggle = () => setCollapsed(v => {
    try { localStorage.setItem("setting_stats_collapsed", String(!v)); } catch (e) { /* preference only */ }
    return !v;
  });
  if (!stats) return null;

  const { total_styles, po_created, po_cancelled = 0, po_pending, avg_days_to_po, pct_created, buyer_rejected, grn_done, grn_full,
          total_req_qty, po_created_qty, grn_qty, buyer_rejected_qty } = stats;
  const pct_pending  = total_styles ? (po_pending  / total_styles * 100).toFixed(1) : 0;
  const pct_cancelled = total_styles ? (po_cancelled / total_styles * 100).toFixed(1) : 0;
  const pct_grn = po_created ? ((grn_done || 0) / po_created * 100).toFixed(1) : 0;
  const fmtQty = (n) => n != null && n > 0 ? n.toLocaleString() + " pcs" : null;
  const pct_created_qty  = total_req_qty ? +((po_created_qty  || 0) / total_req_qty * 100).toFixed(1) : 0;
  const pct_pending_qty  = total_req_qty ? +((Math.max(0, (total_req_qty || 0) - (po_created_qty || 0))) / total_req_qty * 100).toFixed(1) : 0;
  const pct_grn_qty      = total_req_qty ? +((grn_qty || 0) / total_req_qty * 100).toFixed(1) : 0;
  const pct_rejected_qty = total_req_qty ? +((buyer_rejected_qty || 0) / total_req_qty * 100).toFixed(1) : 0;

  return (
    <div style={{ marginBottom: collapsed ? 12 : 20 }}>
      {/* Compact summary bar (always visible) */}
      <div style={{
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        padding: collapsed ? "8px 8px 8px 14px" : "0 0 8px",
        background: collapsed ? "var(--card)" : "transparent",
        border: collapsed ? "1px solid var(--border)" : "none",
        borderRadius: collapsed ? "var(--radius)" : 0,
        boxShadow: collapsed ? "var(--shadow-sm)" : "none",
      }}>
        {collapsed && (
          <div style={{ display: "flex", gap: "6px 20px", flexWrap: "wrap", flex: 1, alignItems: "center", fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
            <span><span style={KL}>Styles </span><strong style={{ color: "var(--text)" }}>{total_styles}</strong></span>
            <span><span style={KL}>PO Created </span><strong style={{ color: "var(--success)" }}>{po_created}</strong><span style={{ color: "var(--text-muted)", fontSize: 11.5 }}> ({pct_created}%)</span></span>
            <span><span style={KL}>Pending </span><strong style={{ color: "var(--danger)" }}>{po_pending}</strong></span>
            {po_cancelled > 0 && <span><span style={KL}>Cancelled </span><strong style={{ color: "var(--warning)" }}>{po_cancelled}</strong></span>}
            {(grn_done || 0) > 0 && <span><span style={KL}>GRN </span><strong>{grn_done}</strong><span style={{ color: "var(--text-muted)", fontSize: 11.5 }}> ({pct_grn}%)</span></span>}
            {avg_days_to_po !== null && <span><span style={KL}>Avg </span><strong style={{ color: avg_days_to_po <= 7 ? "var(--success)" : avg_days_to_po <= 14 ? "var(--warning)" : "var(--danger)" }}>{avg_days_to_po}d</strong></span>}
          </div>
        )}
        <Button variant="ghost" size="sm" onClick={toggle} aria-expanded={!collapsed} style={{ marginLeft: "auto" }}>
          {collapsed ? "Show stats ▾" : "Hide stats ▴"}
        </Button>
      </div>

      {/* Full stats grid */}
      {!collapsed && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Styles</div>
            <div className="stat-value primary">{total_styles}</div>
            {fmtQty(total_req_qty) && <div className="stat-sub" style={{ fontWeight: 600, color: "var(--text)", fontSize: 12 }}>{fmtQty(total_req_qty)} requested</div>}
            {!fmtQty(total_req_qty) && <div className="stat-sub">in this reorder</div>}
          </div>

          <div className="stat-card">
            <div className="stat-label">PO Created</div>
            <div className="stat-value success">{po_created}</div>
            <div className="stat-sub">
              <span className="pct green">{pct_created}% styles</span>
              {pct_created_qty > 0 && <> · <span className="pct green">{pct_created_qty}% qty</span></>}
            </div>
            {fmtQty(po_created_qty) && <div className="stat-sub" style={{ fontWeight: 600, color: "var(--success)", fontSize: 12 }}>{fmtQty(po_created_qty)}</div>}
          </div>

          <div className="stat-card">
            <div className="stat-label">PO Pending</div>
            <div className="stat-value danger">{po_pending}</div>
            <div className="stat-sub">
              <span className="pct red">{pct_pending}% styles</span>
              {pct_pending_qty > 0 && <> · <span className="pct red">{pct_pending_qty}% qty</span></>}
            </div>
          </div>

          {po_cancelled > 0 && (
            <div className="stat-card">
              <div className="stat-label">PO Cancelled</div>
              <div className="stat-value warning">{po_cancelled}</div>
              <div className="stat-sub">
                <span className="pct orange">{pct_cancelled}% styles</span>
              </div>
              <div className="stat-sub">fully cancelled, no GRN</div>
            </div>
          )}

          <div className="stat-card">
            <div className="stat-label">GRN Done</div>
            <div className={`stat-value ${(grn_done || 0) === 0 ? "primary" : (grn_done || 0) >= po_created ? "success" : "warning"}`}>
              {grn_done || 0}
              {grn_full > 0 && grn_full !== grn_done && (
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-muted)", marginLeft: 4 }}>
                  ({grn_full} full)
                </span>
              )}
            </div>
            <div className="stat-sub">
              {po_created > 0
                ? <><span className={`pct ${pct_grn >= 100 ? "green" : pct_grn > 0 ? "orange" : "red"}`}>{pct_grn}% of PO styles</span>
                    {pct_grn_qty > 0 && <> · <span className={`pct ${pct_grn_qty >= 100 ? "green" : pct_grn_qty > 0 ? "orange" : "red"}`}>{pct_grn_qty}% of req qty</span></>}
                  </>
                : "awaiting PO"}
            </div>
            {fmtQty(grn_qty) && <div className="stat-sub" style={{ fontWeight: 600, color: "var(--success)", fontSize: 12 }}>{fmtQty(grn_qty)} received</div>}
          </div>

          {buyer_rejected > 0 && (
            <div className="stat-card">
              <div className="stat-label">Buyer Rejected</div>
              <div className="stat-value warning">{buyer_rejected}</div>
              <div className="stat-sub">
                excluded from pending
                {pct_rejected_qty > 0 && <> · <span className="pct orange">{pct_rejected_qty}% qty</span></>}
              </div>
              {fmtQty(buyer_rejected_qty) && <div className="stat-sub" style={{ fontWeight: 600, color: "var(--warning)", fontSize: 12 }}>{fmtQty(buyer_rejected_qty)}</div>}
            </div>
          )}

          <div className="stat-card">
            <div className="stat-label">Avg Days to PO</div>
            <div className={`stat-value ${avg_days_to_po === null ? "primary" : avg_days_to_po <= 7 ? "success" : avg_days_to_po <= 14 ? "warning" : "danger"}`}>
              {avg_days_to_po !== null ? avg_days_to_po : "—"}
            </div>
            <div className="stat-sub">from request date</div>
          </div>
        </div>
      )}
    </div>
  );
}
